/**
 * client/src/features/income/components/IncomeCards/SalaryIncomeCard.jsx
 * 
 * Displays salary/W2 income information in a card format.
 * Handles secure data display and user interactions.
 */
import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { 
  BriefcaseIcon, 
  CalendarIcon, 
  ChartBarIcon,
  DocumentTextIcon,
  PencilIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import EditIncomeWizard from '../EditIncomeWizard';
import IncomeTypeWizard from '../AddIncome/IncomeTypeWizard';
import useIncome from '../../hooks/useIncome';
import { normalizeIncomeData } from '../../utils/incomeUtils';
import { calculateTaxSummary } from '../shared/taxCalculator';

// Helper function to properly extract ID from income object (moved outside component)
const extractIncomeId = (income) => {
  console.log('🔍 extractIncomeId called with:', income);
  
  if (!income) {
    console.log('🔍 No income provided');
    return null;
  }
  
  // Try _id first
  if (income._id !== undefined) {
    console.log('🔍 Found income._id:', income._id, 'Type:', typeof income._id);
    
    if (typeof income._id === 'string') {
      console.log('🔍 ✅ Using string _id:', income._id);
      return income._id;
    } else if (typeof income._id === 'object' && income._id !== null) {
      console.log('🔍 _id is object, checking properties:', Object.keys(income._id));
      
      // Handle MongoDB ObjectId objects
      if (income._id.toString && typeof income._id.toString === 'function') {
        const stringId = income._id.toString();
        console.log('🔍 ✅ Using _id.toString():', stringId);
        return stringId;
      } else if (income._id.$oid) {
        console.log('🔍 ✅ Using _id.$oid:', income._id.$oid);
        return income._id.$oid;
      } else if (income._id.id) {
        console.log('🔍 ✅ Using _id.id:', income._id.id);
        return income._id.id;
      }
    }
  }
  
  // Try id field
  if (income.id !== undefined) {
    console.log('🔍 Found income.id:', income.id, 'Type:', typeof income.id);
    
    if (typeof income.id === 'string') {
      console.log('🔍 ✅ Using string id:', income.id);
      return income.id;
    } else if (typeof income.id === 'object' && income.id !== null && income.id.toString) {
      const stringId = income.id.toString();
      console.log('🔍 ✅ Using id.toString():', stringId);
      return stringId;
    }
  }
  
  console.log('🔍 ❌ Could not extract valid ID');
  return null;
};

export const SalaryIncomeCard = ({ 
  income,
  onEdit,
  onDelete,
  refreshIncomes,
  className = ''
}) => {
  // Defensive logging to help debug
  console.log('💲 Rendering SalaryIncomeCard with data:', income);
  console.log('💲 Income ID debug:', { 
    _id: income?._id, 
    id: income?.id, 
    _idType: typeof income?._id, 
    idType: typeof income?.id 
  });
  
  // Test the ID extraction function immediately
  const testExtractedId = extractIncomeId(income);
  console.log('💲 Extracted ID test:', testExtractedId, 'Type:', typeof testExtractedId);
  
  // Defensive data handling
  if (!income) {
    console.error('❌ Income data is missing');
    return <div className="p-4 bg-red-50 border border-red-200 rounded-lg">Error: Missing income data</div>;
  }
  
  // State for secure data display and expanded view
  const [showSensitiveData, setShowSensitiveData] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [estimatedTaxes, setEstimatedTaxes] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const { deleteIncome, updateIncome, switchJob } = useIncome();
  const [showIncomeTypeWizard, setShowIncomeTypeWizard] = useState(false);

  // State options for dropdown
  const STATE_OPTIONS = [
    { id: 'AL', label: 'Alabama' },
    { id: 'AK', label: 'Alaska' },
    { id: 'AZ', label: 'Arizona' },
    { id: 'AR', label: 'Arkansas' },
    { id: 'CA', label: 'California' },
    { id: 'CO', label: 'Colorado' },
    { id: 'CT', label: 'Connecticut' },
    { id: 'DE', label: 'Delaware' },
    { id: 'FL', label: 'Florida' },
    { id: 'GA', label: 'Georgia' },
    { id: 'HI', label: 'Hawaii' },
    { id: 'ID', label: 'Idaho' },
    { id: 'IL', label: 'Illinois' },
    { id: 'IN', label: 'Indiana' },
    { id: 'IA', label: 'Iowa' },
    { id: 'KS', label: 'Kansas' },
    { id: 'KY', label: 'Kentucky' },
    { id: 'LA', label: 'Louisiana' },
    { id: 'ME', label: 'Maine' },
    { id: 'MD', label: 'Maryland' },
    { id: 'MA', label: 'Massachusetts' },
    { id: 'MI', label: 'Michigan' },
    { id: 'MN', label: 'Minnesota' },
    { id: 'MS', label: 'Mississippi' },
    { id: 'MO', label: 'Missouri' },
    { id: 'MT', label: 'Montana' },
    { id: 'NE', label: 'Nebraska' },
    { id: 'NV', label: 'Nevada' },
    { id: 'NH', label: 'New Hampshire' },
    { id: 'NJ', label: 'New Jersey' },
    { id: 'NM', label: 'New Mexico' },
    { id: 'NY', label: 'New York' },
    { id: 'NC', label: 'North Carolina' },
    { id: 'ND', label: 'North Dakota' },
    { id: 'OH', label: 'Ohio' },
    { id: 'OK', label: 'Oklahoma' },
    { id: 'OR', label: 'Oregon' },
    { id: 'PA', label: 'Pennsylvania' },
    { id: 'RI', label: 'Rhode Island' },
    { id: 'SC', label: 'South Carolina' },
    { id: 'SD', label: 'South Dakota' },
    { id: 'TN', label: 'Tennessee' },
    { id: 'TX', label: 'Texas' },
    { id: 'UT', label: 'Utah' },
    { id: 'VT', label: 'Vermont' },
    { id: 'VA', label: 'Virginia' },
    { id: 'WA', label: 'Washington' },
    { id: 'WV', label: 'West Virginia' },
    { id: 'WI', label: 'Wisconsin' },
    { id: 'WY', label: 'Wyoming' },
    { id: 'DC', label: 'District of Columbia' }
  ];
  
  // Use our utility function to normalize income data
  const { grossAmount, netAmount, frequency, taxes: normalizedTaxes, type } = normalizeIncomeData(income);
  
  // Create the full normalized income object with additional fields
  const normalizedIncome = {
    _id: extractIncomeId(income) || 'unknown',
    name: income.name || 'Unnamed Income',
    grossAmount,
    grossAnnualSalary: income.grossAnnualSalary || income.typeSpecificDetails?.salary?.payDetails?.grossAnnualSalary || null,
    netAmount,
    frequency,
    startDate: income.startDate ? new Date(income.startDate) : new Date(),
    endDate: income.endDate ? new Date(income.endDate) : null,
    taxes: normalizedTaxes,
    type,
    // Pass along existing bucket info
    bucketId: income.bucketId,
    bucket: income.bucket,
    // Add remaining fields specific to salary income
    deductions: income.typeSpecificDetails?.salary?.deductions ? {
      retirement: income.typeSpecificDetails.salary.deductions.retirement || 0,
      health: income.typeSpecificDetails.salary.deductions.health || 0,
      dental: income.typeSpecificDetails.salary.deductions.dental || 0,
      vision: income.typeSpecificDetails.salary.deductions.vision || 0,
      hsa: income.typeSpecificDetails.salary.deductions.hsa || 0,
      fsa: income.typeSpecificDetails.salary.deductions.fsa || 0,
      other: income.typeSpecificDetails.salary.deductions.other || 0
    } : (income.typeSpecificDetails?.deductions ? {
      retirement: income.typeSpecificDetails.deductions.retirement || 0,
      health: income.typeSpecificDetails.deductions.health || 0,
      dental: income.typeSpecificDetails.deductions.dental || 0,
      vision: income.typeSpecificDetails.deductions.vision || 0,
      hsa: income.typeSpecificDetails.deductions.hsa || 0,
      fsa: income.typeSpecificDetails.deductions.fsa || 0,
      other: income.typeSpecificDetails.deductions.other || 0
    } : {}),
    w4: income.typeSpecificDetails?.salary?.w4 ? {
      filingStatus: income.typeSpecificDetails.salary.w4.filingStatus || 'SINGLE',
      dependents: income.typeSpecificDetails.salary.w4.dependents || 0,
      otherIncome: income.typeSpecificDetails.salary.w4.otherIncome || 0,
      deductions: income.typeSpecificDetails.salary.w4.deductions || 0,
      extraWithholding: income.typeSpecificDetails.salary.w4.extraWithholding || 0
    } : (income.typeSpecificDetails?.w4 ? {
      filingStatus: income.typeSpecificDetails.w4.filingStatus || 'SINGLE',
      dependents: income.typeSpecificDetails.w4.dependents || 0,
      otherIncome: income.typeSpecificDetails.w4.otherIncome || 0,
      deductions: income.typeSpecificDetails.w4.deductions || 0,
      extraWithholding: income.typeSpecificDetails.w4.extraWithholding || 0
    } : {}),
    employer: income.typeSpecificDetails?.salary?.employer?.name || 
             income.typeSpecificDetails?.employer?.name || 
             'Not specified',
    location: {
      state: income.typeSpecificDetails?.salary?.employer?.location?.state || 
             income.typeSpecificDetails?.employer?.location?.state || 
             'Not specified'
    },
    // Equity (if present)
    equity: income.typeSpecificDetails?.salary?.equity ||
            income.typeSpecificDetails?.equity ||
            income.equity || null,
    description: income.description || '',
  };

  // Calculate estimated taxes if not provided
  useEffect(() => {
    if (!hasTaxData(normalizedIncome.taxes)) {
      const estimatedTaxData = calculateEstimatedTaxes(normalizedIncome.grossAmount, normalizedIncome.frequency, normalizedIncome.location.state);
      setEstimatedTaxes(estimatedTaxData);
    }
  }, [normalizedIncome.grossAmount, normalizedIncome.frequency, normalizedIncome.location.state]);

  // Check if we have tax data or need to use estimates
  const hasTaxData = (taxes) => {
    return taxes && 
      (taxes.federal > 0 || 
       taxes.state > 0 || 
       taxes.socialSecurity > 0 || 
       taxes.medicare > 0);
  };

  // Calculate estimated taxes based on gross amount
  const calculateEstimatedTaxes = (grossAmount, frequency, state) => {
    // Convert to annual amount for standardized calculations
    const annualGross = calculateAnnualAmount(grossAmount, frequency);
    
    // Basic tax rate estimates (simplified)
    const fedTaxRate = estimateFederalTaxRate(annualGross);
    const stateTaxRate = estimateStateTaxRate(annualGross, state);
    
    // Standard percentages
    const socialSecurityRate = 0.062; // 6.2%
    const medicareRate = 0.0145; // 1.45%
    
    // Calculate tax amounts
    const federalTax = annualGross * fedTaxRate;
    const stateTax = annualGross * stateTaxRate;
    const socialSecurityTax = Math.min(annualGross * socialSecurityRate, 9114); // 2023 cap
    const medicareTax = annualGross * medicareRate;
    
    // Calculate per-pay-period amounts based on frequency
    const frequencyDivisor = getFrequencyDivisor(frequency);
    
    return {
      federal: federalTax / frequencyDivisor,
      state: stateTax / frequencyDivisor,
      socialSecurity: socialSecurityTax / frequencyDivisor,
      medicare: medicareTax / frequencyDivisor,
      local: 0,
      other: 0,
      total: (federalTax + stateTax + socialSecurityTax + medicareTax) / frequencyDivisor,
      isEstimated: true
    };
  };

  // Helper to calculate annual amount from any frequency
  const calculateAnnualAmount = (amount, frequency) => {
    switch (frequency) {
      case 'WEEKLY':
        return amount * 52;
      case 'BI_WEEKLY':
        return amount * 26;
      case 'MONTHLY':
        return amount * 12;
      case 'ANNUALLY':
        return amount;
      default:
        return amount * 12; // Default to monthly
    }
  };

  // Helper to get divisor for converting annual to per-pay-period
  const getFrequencyDivisor = (frequency) => {
    switch (frequency) {
      case 'WEEKLY':
        return 52;
      case 'BI_WEEKLY':
        return 26;
      case 'MONTHLY':
        return 12;
      case 'ANNUALLY':
        return 1;
      default:
        return 12; // Default to monthly
    }
  };

  // Simplified federal tax rate estimator
  const estimateFederalTaxRate = (annualGross) => {
    if (annualGross <= 11000) return 0.10;
    if (annualGross <= 44725) return 0.12;
    if (annualGross <= 95375) return 0.22;
    if (annualGross <= 182100) return 0.24;
    if (annualGross <= 231250) return 0.32;
    if (annualGross <= 578125) return 0.35;
    return 0.37;
  };

  // Simplified state tax rate estimator
  const estimateStateTaxRate = (annualGross, state) => {
    // Simplified state tax rates (examples)
    const stateTaxes = {
      'CA': 0.093,
      'NY': 0.065,
      'TX': 0,
      'FL': 0,
      'WA': 0,
      'IL': 0.0495,
      'PA': 0.0307,
      'OH': 0.0399,
      'MI': 0.0425,
      'GA': 0.0575
    };
    
    // Return state tax or default
    return stateTaxes[state] || 0.05; // Default to 5% if state not found
  };
  
  // Small UI helpers for detail layout
  const DetailItem = ({ label, children }) => (
    <div>
      <p className="text-sm text-gray-700">{label}</p>
      <p className="text-base md:text-lg font-semibold text-gray-900">{children}</p>
    </div>
  );

  const StatChip = ({ label, value, tone = 'indigo' }) => {
    const toneMap = {
      indigo: {
        bg: 'bg-indigo-50',
        text: 'text-indigo-700',
        value: 'text-indigo-800'
      },
      blue: {
        bg: 'bg-blue-50',
        text: 'text-blue-700',
        value: 'text-blue-800'
      },
      emerald: {
        bg: 'bg-emerald-50',
        text: 'text-emerald-700',
        value: 'text-emerald-800'
      }
    };
    const t = toneMap[tone] || toneMap.indigo;
    return (
      <div className={`rounded-lg ${t.bg} px-3 py-2 border border-gray-100` }>
        <div className={`text-sm ${t.text}`}>{label}</div>
        <div className={`text-lg font-semibold ${t.value}`}>{value}</div>
      </div>
    );
  };

  const Section = ({ title, right, children }) => (
    <div className="mb-5">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-base font-semibold text-gray-800">{title}</h3>
        {right ? <div className="flex items-center gap-2">{right}</div> : null}
      </div>
      {children}
    </div>
  );
  
  // Format currency with masking for sensitive data
  const formatCurrency = (amount, isSensitive = false) => {
    if (!amount) return '$0.00';
    if (isSensitive && !showSensitiveData) return '••••••';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Calculate monthly equivalent for different frequencies
  const calculateMonthlyAmount = (amount, frequency) => {
    if (!amount) return 0;
    switch (frequency) {
      case 'WEEKLY':
        return amount * 52 / 12;
      case 'BI_WEEKLY':
        return amount * 26 / 12;
      case 'MONTHLY':
        return amount;
      case 'ANNUALLY':
        return amount / 12;
      default:
        return amount;
    }
  };

  // Format the payment frequency for display
  const formatFrequency = (freq) => {
    switch (freq) {
      case 'WEEKLY': return 'Weekly';
      case 'BI_WEEKLY': return 'Bi-Weekly';
      case 'MONTHLY': return 'Monthly';
      case 'ANNUALLY': return 'Annually';
      default: return freq;
    }
  };

  // Handle delete confirmation
  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    try {
      // Extract bucketId from income
      const bucketId = normalizedIncome.bucketId;
      
      // Delete the income
      await deleteIncome(bucketId, normalizedIncome._id);
      setShowDeleteConfirm(false);
      
      // Refresh the income list
      if (refreshIncomes) {
        refreshIncomes();
      }
    } catch (error) {
      console.error('Failed to delete income:', error);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  // Toggle expanded details view
  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };
  
  // Update button opens the wizard
  const handleUpdateClick = () => {
    console.log('Update button clicked for income:', normalizedIncome._id);
    setShowWizard(true);
    setIsExpanded(true);

    // Get current tax and deduction data
    const currentTaxes = getTaxes();
    const currentDeductions = normalizedIncome.deductions || {};
    
    // Initialize edit form data with ALL current values
    setEditFormData({
      // Primary Details
      name: normalizedIncome.name,
      startDate: normalizedIncome.startDate ? format(normalizedIncome.startDate, 'yyyy-MM-dd') : '',
      endDate: normalizedIncome.endDate ? format(normalizedIncome.endDate, 'yyyy-MM-dd') : '',
      employer: normalizedIncome.employer,
      state: normalizedIncome.location?.state || '',
      
      // Salary Details
      grossAmount: normalizedIncome.grossAmount,
      grossAnnualSalary: normalizedIncome.grossAnnualSalary || calculateAnnualAmount(normalizedIncome.grossAmount, normalizedIncome.frequency),
      netAmount: normalizedIncome.netAmount,
      frequency: normalizedIncome.frequency,
      
      // W4 Information
      filingStatus: normalizedIncome.w4?.filingStatus || 'SINGLE',
      dependents: normalizedIncome.w4?.dependents || 0,
      otherIncome: normalizedIncome.w4?.otherIncome || 0,
      w4Deductions: normalizedIncome.w4?.deductions || 0,
      extraWithholding: normalizedIncome.w4?.extraWithholding || 0,
      
      // Tax Information (only if not estimated)
      federalTax: !currentTaxes.isEstimated ? currentTaxes.federal || 0 : 0,
      stateTax: !currentTaxes.isEstimated ? currentTaxes.state || 0 : 0,
      socialSecurityTax: !currentTaxes.isEstimated ? currentTaxes.socialSecurity || 0 : 0,
      medicareTax: !currentTaxes.isEstimated ? currentTaxes.medicare || 0 : 0,
      localTax: !currentTaxes.isEstimated ? currentTaxes.local || 0 : 0,
      otherTax: !currentTaxes.isEstimated ? currentTaxes.other || 0 : 0,
      
      // Deductions
      retirementDeduction: currentDeductions.retirement || 0,
      healthDeduction: currentDeductions.health || 0,
      dentalDeduction: currentDeductions.dental || 0,
      visionDeduction: currentDeductions.vision || 0,
      otherDeduction: currentDeductions.other || 0,
      
      // Notes
      description: normalizedIncome.description || ''
    });
  };

  // Helper to initialize edit form (used by Update->Manual and direct Edit)
  const initializeEditForm = () => {
    setEditFormData({
      name: normalizedIncome.name,
      startDate: normalizedIncome.startDate ? format(normalizedIncome.startDate, 'yyyy-MM-dd') : '',
      endDate: normalizedIncome.endDate ? format(normalizedIncome.endDate, 'yyyy-MM-dd') : '',
      employer: normalizedIncome.employer,
      state: normalizedIncome.location?.state || '',
      grossAmount: normalizedIncome.grossAmount,
      grossAnnualSalary: calculateAnnualAmount(normalizedIncome.grossAmount, normalizedIncome.frequency),
      netAmount: normalizedIncome.netAmount,
      frequency: normalizedIncome.frequency,
      filingStatus: normalizedIncome.w4?.filingStatus || 'SINGLE',
      dependents: normalizedIncome.w4?.dependents || 0,
      otherIncome: normalizedIncome.w4?.otherIncome || 0,
      w4Deductions: normalizedIncome.w4?.deductions || 0,
      extraWithholding: normalizedIncome.w4?.extraWithholding || 0,
      retirementDeduction: normalizedIncome.deductions?.retirement || 0,
      healthDeduction: normalizedIncome.deductions?.health || 0,
      dentalDeduction: normalizedIncome.deductions?.dental || 0,
      visionDeduction: normalizedIncome.deductions?.vision || 0,
      otherDeduction: normalizedIncome.deductions?.other || 0,
      description: normalizedIncome.description || ''
    });
  };

  // Edit button now opens manual edit (pre-populated)
  const handleEditClick = () => {
    setShowWizard(true);
    setIsExpanded(true);
  };

  const handleWizardCancel = () => setShowWizard(false);
  const handleWizardManual = () => {
    // Immediately open the full IncomeTypeWizard centered and pre-populated
    setShowWizard(false);
    setTimeout(() => setShowIncomeTypeWizard(true), 0);
  };
  const handleWizardRaise = async (form) => {
    setShowWizard(false);
    setIsEditMode(true);
    setEditFormData(prev => ({
      ...prev,
      effectiveDate: form.effectiveDate,
      grossAmount: form.newBase || prev.grossAmount,
      description: prev.description || ''
    }));
  };
  const handleWizardJobChange = async (form) => {
    try {
      setShowWizard(false);
      // Map frequency to backend values
      const freqMap = {
        weekly: 'weekly',
        biweekly: 'biweekly',
        'semi-monthly': 'monthly',
        monthly: 'monthly',
        yearly: 'yearly'
      };
      const payload = {
        startDate: form.startDate,
        employer: form.employer,
        basePerPeriod: form.basePerPeriod,
        grossAnnual: form.grossAnnual,
        frequency: freqMap[`${(form.frequency || '').toLowerCase()}`] || 'monthly',
        signingBonus: form.signingBonus,
        bonusTargetPercent: form.bonusTargetPercent,
        bonusTargetFixed: form.bonusTargetFixed,
        rsu: form.rsu,
        vestCliffMonths: form.vestCliffMonths,
        vestMonths: form.vestMonths,
        estSharePrice: form.estSharePrice,
        location: form.location,
        jobSite: form.jobSite
      };
      await switchJob(form.replaceIncomeId || normalizedIncome._id, payload);
      // After switching jobs, keep view collapsed and show success
      setIsEditMode(false);
      if (refreshIncomes) await refreshIncomes();
    } catch (e) {
      console.error('Failed to switch job:', e);
    }
  };

  const handleEditCancel = () => {
    setIsEditMode(false);
    setEditFormData({});
  };

  const handleEditSave = async () => {
    try {
      setIsSaving(true);
      
      // Prepare update data with ALL fields in the expected format
      const updateData = {
        name: editFormData.name,
        amount: parseFloat(editFormData.grossAmount), // Server expects 'amount' field
        grossAmount: parseFloat(editFormData.grossAmount),
        grossAnnualSalary: parseFloat(editFormData.grossAnnualSalary),
        netAmount: parseFloat(editFormData.netAmount),
        frequency: (editFormData.frequency || 'monthly').toLowerCase(),
        startDate: editFormData.startDate,
        endDate: editFormData.endDate || null,
        description: editFormData.description,
        
        // Tax withholdings (only include if user has provided actual values)
        taxes: {
          federal: parseFloat(editFormData.federalTax) || 0,
          state: parseFloat(editFormData.stateTax) || 0,
          socialSecurity: parseFloat(editFormData.socialSecurityTax) || 0,
          medicare: parseFloat(editFormData.medicareTax) || 0,
          local: parseFloat(editFormData.localTax) || 0,
          other: parseFloat(editFormData.otherTax) || 0
        },
        
        // Deductions
        deductions: {
          retirement: parseFloat(editFormData.retirementDeduction) || 0,
          health: parseFloat(editFormData.healthDeduction) || 0,
          dental: parseFloat(editFormData.dentalDeduction) || 0,
          vision: parseFloat(editFormData.visionDeduction) || 0,
          other: parseFloat(editFormData.otherDeduction) || 0
        },
        
        typeSpecificDetails: {
          salary: {
            employer: {
              name: editFormData.employer,
              location: {
                state: editFormData.state
              }
            },
            w4: {
              filingStatus: editFormData.filingStatus,
              dependents: parseInt(editFormData.dependents) || 0,
              otherIncome: parseFloat(editFormData.otherIncome) || 0,
              deductions: parseFloat(editFormData.w4Deductions) || 0,
              extraWithholding: parseFloat(editFormData.extraWithholding) || 0
            },
            deductions: {
              retirement: parseFloat(editFormData.retirementDeduction) || 0,
              health: parseFloat(editFormData.healthDeduction) || 0,
              dental: parseFloat(editFormData.dentalDeduction) || 0,
              vision: parseFloat(editFormData.visionDeduction) || 0,
              other: parseFloat(editFormData.otherDeduction) || 0
            }
          }
        }
      };

      // Ensure we have a valid string ID
      const incomeId = extractIncomeId(income);
      console.log('🔧 === SAVE DEBUG START ===');
      console.log('🔧 Income object keys:', Object.keys(income));
      console.log('🔧 income._id:', income._id, 'Type:', typeof income._id);
      console.log('🔧 income.id:', income.id, 'Type:', typeof income.id);
      console.log('🔧 Extracted ID:', incomeId, 'Type:', typeof incomeId);
      console.log('🔧 Full income object:', JSON.stringify(income, null, 2));
      console.log('🔧 === SAVE DEBUG END ===');
      
      if (!incomeId || typeof incomeId !== 'string') {
        console.error('❌ ID extraction failed!');
        console.error('❌ Original income:', income);
        console.error('❌ Extracted ID:', incomeId);
        throw new Error('Invalid income ID - extractIncomeId returned: ' + JSON.stringify(incomeId) + ' from income: ' + JSON.stringify(income));
      }
      
      // Extract bucketId for the updateIncome call
      const bucketId = income.bucketId || 
        (income.bucket?._id) || 
        income.bucket || 
        null;
      
      console.log('🔧 Using bucketId:', bucketId, 'incomeId:', incomeId);
      
      // Call updateIncome with correct parameter order: (bucketId, incomeId, incomeData)
      await updateIncome(bucketId, incomeId, updateData);
      setIsEditMode(false);
      setEditFormData({});
      
      // Refresh the income data
      if (refreshIncomes) {
        await refreshIncomes();
      }
      
      console.log('Income updated successfully');
    } catch (error) {
      console.error('Error updating income:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditFieldChange = (field, value) => {
    setEditFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };
      
      // Handle bidirectional calculation between gross pay and gross annual salary
      if (field === 'grossAmount') {
        const grossAmount = parseFloat(value) || 0;
        const frequency = newData.frequency || normalizedIncome.frequency || 'MONTHLY';
        
        // Validation: Check for reasonable gross amount limits
        if (grossAmount < 0) {
          console.warn('Gross amount cannot be negative');
          return newData;
        }
        if (grossAmount > 1000000) {
          console.warn('Gross amount exceeds reasonable limit');
        }
        
        if (grossAmount && frequency) {
          let annualAmount = 0;
          switch (frequency.toUpperCase()) {
            case 'WEEKLY':
              annualAmount = grossAmount * 52;
              break;
            case 'BI_WEEKLY':
              annualAmount = grossAmount * 26;
              break;
            case 'MONTHLY':
              annualAmount = grossAmount * 12;
              break;
            case 'ANNUALLY':
              annualAmount = grossAmount;
              break;
            default:
              console.warn('Unknown frequency:', frequency);
              return newData;
          }
          
          // Validation: Check for reasonable annual salary limits
          if (annualAmount > 10000000) {
            console.warn('Annual salary exceeds reasonable limit');
          }
          
          newData.grossAnnualSalary = annualAmount.toFixed(2);
        }
      } else if (field === 'grossAnnualSalary') {
        const annualAmount = parseFloat(value) || 0;
        const frequency = newData.frequency || normalizedIncome.frequency || 'MONTHLY';
        
        // Validation: Check for reasonable annual salary limits
        if (annualAmount < 0) {
          console.warn('Annual salary cannot be negative');
          return newData;
        }
        if (annualAmount > 10000000) {
          console.warn('Annual salary exceeds reasonable limit');
        }
        
        if (annualAmount && frequency) {
          let grossAmount = 0;
          switch (frequency.toUpperCase()) {
            case 'WEEKLY':
              grossAmount = annualAmount / 52;
              break;
            case 'BI_WEEKLY':
              grossAmount = annualAmount / 26;
              break;
            case 'MONTHLY':
              grossAmount = annualAmount / 12;
              break;
            case 'ANNUALLY':
              grossAmount = annualAmount;
              break;
            default:
              console.warn('Unknown frequency:', frequency);
              return newData;
          }
          
          // Validation: Check for reasonable gross amount limits
          if (grossAmount > 1000000) {
            console.warn('Calculated gross amount exceeds reasonable limit');
          }
          
          newData.grossAmount = grossAmount.toFixed(2);
        }
      } else if (field === 'frequency') {
        // When frequency changes, update both gross amount and annual salary based on what was last changed
        const currentGrossAmount = parseFloat(newData.grossAmount || normalizedIncome.grossAmount) || 0;
        if (currentGrossAmount && value) {
          let annualAmount = 0;
          switch (value.toUpperCase()) {
            case 'WEEKLY':
              annualAmount = currentGrossAmount * 52;
              break;
            case 'BI_WEEKLY':
              annualAmount = currentGrossAmount * 26;
              break;
            case 'MONTHLY':
              annualAmount = currentGrossAmount * 12;
              break;
            case 'ANNUALLY':
              annualAmount = currentGrossAmount;
              break;
          }
          newData.grossAnnualSalary = annualAmount.toFixed(2);
        }
      }
      
      // Auto-calculate net pay when relevant fields change
      if (['grossAmount', 'grossAnnualSalary', 'frequency', 'state', 'filingStatus', 'dependents', 'otherIncome', 'w4Deductions', 'extraWithholding'].includes(field)) {
        const grossAmount = parseFloat(field === 'grossAmount' ? value : newData.grossAmount || normalizedIncome.grossAmount) || 0;
        const frequency = field === 'frequency' ? value : (newData.frequency || normalizedIncome.frequency || 'MONTHLY');
        const state = field === 'state' ? value : (newData.state || normalizedIncome.location?.state || '');
        const filingStatus = field === 'filingStatus' ? value : (newData.filingStatus || normalizedIncome.w4?.filingStatus || 'single');
        const dependents = parseInt(field === 'dependents' ? value : (newData.dependents || normalizedIncome.w4?.dependents || 0)) || 0;
        const otherIncome = parseFloat(field === 'otherIncome' ? value : (newData.otherIncome || normalizedIncome.w4?.otherIncome || 0)) || 0;
        const w4Deductions = parseFloat(field === 'w4Deductions' ? value : (newData.w4Deductions || normalizedIncome.w4?.deductions || 0)) || 0;
        const extraWithholding = parseFloat(field === 'extraWithholding' ? value : (newData.extraWithholding || normalizedIncome.w4?.extraWithholding || 0)) || 0;
        
        if (grossAmount > 0 && frequency && state) {
          // Calculate tax summary using the same logic as New Income Form
          const taxSummary = calculateTaxSummary({
            grossPay: grossAmount,
            frequency: frequency.toLowerCase(),
            state,
            filingStatus,
            dependents,
            otherIncome,
            deductions: w4Deductions,
            extraWithholding
          });
          
          // Calculate deductions
          const totalDeductions = 
            (parseFloat(newData.retirementDeduction) || 0) +
            (parseFloat(newData.healthDeduction) || 0) +
            (parseFloat(newData.dentalDeduction) || 0) +
            (parseFloat(newData.visionDeduction) || 0) +
            (parseFloat(newData.otherDeduction) || 0);
          
          // Calculate frequency multiplier for period-based amounts
          const getFrequencyMultiplier = (freq) => {
            const multipliers = {
              weekly: 52,
              biweekly: 26,
              'bi_weekly': 26,
              semimonthly: 24,
              monthly: 12,
              quarterly: 4,
              annually: 1,
              yearly: 1
            };
            return multipliers[freq.toLowerCase()] || 12;
          };
          
          const periodMultiplier = getFrequencyMultiplier(frequency);
          const periodTotalTax = taxSummary.totalTax / periodMultiplier;
          const periodDeductions = totalDeductions / periodMultiplier;
          
          // Calculate net pay (gross - taxes - deductions)
          const calculatedNetPay = grossAmount - periodTotalTax - periodDeductions;
          
          // Update calculated fields
          newData.netAmount = Math.max(0, calculatedNetPay).toFixed(2);
          newData.federalTax = (taxSummary.federalTax / periodMultiplier).toFixed(2);
          newData.stateTax = (taxSummary.stateTax / periodMultiplier).toFixed(2);
          newData.socialSecurityTax = ((taxSummary.fica * 0.5) / periodMultiplier).toFixed(2); // Half of FICA is Social Security
          newData.medicareTax = ((taxSummary.fica * 0.5) / periodMultiplier).toFixed(2); // Half of FICA is Medicare
        }
      }
      
      return newData;
    });
  };

  // Get tax data from the income object or calculate estimated taxes
  const getTaxes = () => {
    // Check if we have actual tax data in the income object
    if (normalizedIncome.taxes && hasTaxData(normalizedIncome.taxes)) {
      console.log('✅ Using actual tax data from income:', normalizedIncome.taxes);
      return {
        ...normalizedIncome.taxes,
        isEstimated: false
      };
    } else if (normalizedIncome.taxWithholdings && hasTaxData(normalizedIncome.taxWithholdings)) {
      console.log('✅ Using tax withholdings from income:', normalizedIncome.taxWithholdings);
      return {
        ...normalizedIncome.taxWithholdings,
        isEstimated: false
      };
    } else if (estimatedTaxes) {
      console.log('⚠️ Using estimated tax data:', estimatedTaxes);
      return estimatedTaxes;
    }
    console.log('⚠️ No tax data found for income:', normalizedIncome);
    return {
      federal: 0,
      state: 0,
      socialSecurity: 0,
      medicare: 0,
      local: 0,
      other: 0,
      total: 0,
      isEstimated: true
    };
  };

  const taxes = getTaxes();
  const monthlyGross = calculateMonthlyAmount(normalizedIncome.grossAmount, normalizedIncome.frequency);
  
  // Calculate net pay if not provided
  const actualNetAmount = normalizedIncome.netAmount || (normalizedIncome.grossAmount - taxes.total);
  const monthlyNet = calculateMonthlyAmount(actualNetAmount, normalizedIncome.frequency);

  // Add additional debug logging
  console.log('🔍 SalaryIncomeCard normalized income data:', {
    id: normalizedIncome._id,
    name: normalizedIncome.name,
    type: normalizedIncome.type,
    grossAmount: normalizedIncome.grossAmount,
    netAmount: normalizedIncome.netAmount,
    taxes,
    employer: normalizedIncome.employer || normalizedIncome.employerName,
    w4: normalizedIncome.w4
  });

  // Calculate estimated annual income
  const annualGross = monthlyGross * 12;
  const annualNet = monthlyNet * 12;

  // Calculate effective tax rate if we have both gross and net
  const effectiveTaxRate = normalizedIncome.grossAmount > 0 
    ? ((normalizedIncome.grossAmount - actualNetAmount) / normalizedIncome.grossAmount) * 100
    : 0;

  // When expanded is true, the component renders additional details:
  const renderExpandedDetails = () => {
    if (!isExpanded) return null;
    
    // Get tax data from either actual data or estimates
    const taxData = getTaxes();
    const deductions = normalizedIncome.deductions || {};
    
    // Calculate totals
    const totalTax = 
      (taxData.federal || 0) + 
      (taxData.state || 0) + 
      (taxData.socialSecurity || 0) + 
      (taxData.medicare || 0);
    
    const totalDeductions = 
      (deductions.retirement || 0) + 
      (deductions.health || 0) + 
      (deductions.dental || 0) + 
      (deductions.vision || 0) + 
      (deductions.other || 0);
    
    const effectiveTaxRate = normalizedIncome.grossAmount > 0 
      ? ((totalTax / normalizedIncome.grossAmount) * 100).toFixed(1) 
      : 0;
    
    if (isEditMode) {
      // Edit Mode - ALL fields editable
      return (
        <div className="p-4 bg-gray-50 border-t border-gray-200">
          {/* Primary Details Section - Editable (Basic Information) */}
          <div className="mb-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Primary Details</h3>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Income Name</label>
                <input
                  type="text"
                  value={editFormData.name || ''}
                  onChange={(e) => handleEditFieldChange('name', e.target.value)}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  placeholder="Income name"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Start Date</label>
                <input
                  type="date"
                  value={editFormData.startDate || ''}
                  onChange={(e) => handleEditFieldChange('startDate', e.target.value)}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Employer</label>
                <input
                  type="text"
                  value={editFormData.employer || ''}
                  onChange={(e) => handleEditFieldChange('employer', e.target.value)}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  placeholder="Employer name"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Location (State)</label>
                <select
                  value={editFormData.state || ''}
                  onChange={(e) => handleEditFieldChange('state', e.target.value)}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                >
                  <option value="">Select a state</option>
                  {STATE_OPTIONS.map(option => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          
          {/* Payment Details - Editable */}
          <div className="mb-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Payment Details</h3>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Gross Annual Salary</label>
                <input
                  type="number"
                  step="0.01"
                  value={editFormData.grossAnnualSalary || ''}
                  onChange={(e) => handleEditFieldChange('grossAnnualSalary', e.target.value)}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Net Annual Salary (estimated)</label>
                <input
                  type="number"
                  step="0.01"
                  value={annualNet || ''}
                  readOnly
                  className="w-full px-2 py-1 border border-gray-200 bg-gray-50 rounded text-sm"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Payment Frequency</label>
                <select
                  value={editFormData.frequency || ''}
                  onChange={(e) => handleEditFieldChange('frequency', e.target.value)}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                >
                  <option value="WEEKLY">Weekly</option>
                  <option value="BI_WEEKLY">Bi-Weekly</option>
                  <option value="MONTHLY">Monthly</option>
                  <option value="ANNUALLY">Annually</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Gross Per Pay Period</label>
                <input
                  type="number"
                  step="0.01"
                  value={editFormData.grossAmount || ''}
                  onChange={(e) => handleEditFieldChange('grossAmount', e.target.value)}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Net Per Pay Period (estimated)</label>
                <input
                  type="number"
                  step="0.01"
                  value={editFormData.netAmount || ''}
                  onChange={(e) => handleEditFieldChange('netAmount', e.target.value)}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          {/* W4 Information Section - Editable */}
          <div className="mb-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">W4 Information</h3>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Filing Status</label>
                <select
                  value={editFormData.filingStatus || ''}
                  onChange={(e) => handleEditFieldChange('filingStatus', e.target.value)}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                >
                  <option value="SINGLE">Single</option>
                  <option value="MARRIED">Married Filing Jointly</option>
                  <option value="MARRIED_SEPARATELY">Married Filing Separately</option>
                  <option value="HEAD">Head of Household</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Dependents</label>
                <input
                  type="number"
                  value={editFormData.dependents || ''}
                  onChange={(e) => handleEditFieldChange('dependents', e.target.value)}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  placeholder="0"
                  min="0"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Other Income</label>
                <input
                  type="number"
                  step="0.01"
                  value={editFormData.otherIncome || ''}
                  onChange={(e) => handleEditFieldChange('otherIncome', e.target.value)}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">W4 Deductions</label>
                <input
                  type="number"
                  step="0.01"
                  value={editFormData.w4Deductions || ''}
                  onChange={(e) => handleEditFieldChange('w4Deductions', e.target.value)}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  placeholder="0.00"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-gray-600 mb-1">Extra Withholding</label>
                <input
                  type="number"
                  step="0.01"
                  value={editFormData.extraWithholding || ''}
                  onChange={(e) => handleEditFieldChange('extraWithholding', e.target.value)}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          {/* Deductions Section - Editable */}
          <div className="mb-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Deductions</h3>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Retirement</label>
                <input
                  type="number"
                  step="0.01"
                  value={editFormData.retirementDeduction || ''}
                  onChange={(e) => handleEditFieldChange('retirementDeduction', e.target.value)}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Health Insurance</label>
                <input
                  type="number"
                  step="0.01"
                  value={editFormData.healthDeduction || ''}
                  onChange={(e) => handleEditFieldChange('healthDeduction', e.target.value)}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Dental Insurance</label>
                <input
                  type="number"
                  step="0.01"
                  value={editFormData.dentalDeduction || ''}
                  onChange={(e) => handleEditFieldChange('dentalDeduction', e.target.value)}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Vision Insurance</label>
                <input
                  type="number"
                  step="0.01"
                  value={editFormData.visionDeduction || ''}
                  onChange={(e) => handleEditFieldChange('visionDeduction', e.target.value)}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  placeholder="0.00"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-gray-600 mb-1">Other Deductions</label>
                <input
                  type="number"
                  step="0.01"
                  value={editFormData.otherDeduction || ''}
                  onChange={(e) => handleEditFieldChange('otherDeduction', e.target.value)}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          {/* Tax Information Section - Editable */}
          <div className="mb-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">
              Tax Information
              <span className="text-xs text-gray-500 ml-2">(Auto-calculated; override if needed)</span>
            </h3>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Federal Tax</label>
                <input
                  type="number"
                  step="0.01"
                  value={editFormData.federalTax || ''}
                  onChange={(e) => handleEditFieldChange('federalTax', e.target.value)}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">State Tax</label>
                <input
                  type="number"
                  step="0.01"
                  value={editFormData.stateTax || ''}
                  onChange={(e) => handleEditFieldChange('stateTax', e.target.value)}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Social Security</label>
                <input
                  type="number"
                  step="0.01"
                  value={editFormData.socialSecurityTax || ''}
                  onChange={(e) => handleEditFieldChange('socialSecurityTax', e.target.value)}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Medicare</label>
                <input
                  type="number"
                  step="0.01"
                  value={editFormData.medicareTax || ''}
                  onChange={(e) => handleEditFieldChange('medicareTax', e.target.value)}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Local Tax</label>
                <input
                  type="number"
                  step="0.01"
                  value={editFormData.localTax || ''}
                  onChange={(e) => handleEditFieldChange('localTax', e.target.value)}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Other Tax</label>
                <input
                  type="number"
                  step="0.01"
                  value={editFormData.otherTax || ''}
                  onChange={(e) => handleEditFieldChange('otherTax', e.target.value)}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          {/* Notes Section - Editable */}
          <div className="mb-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Notes</h3>
            <textarea
              value={editFormData.description || ''}
              onChange={(e) => handleEditFieldChange('description', e.target.value)}
              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
              rows="2"
              placeholder="Additional notes..."
            />
          </div>



          {/* Estimated Tax Breakdown Section - Read-only during edit */}
          <div className="mb-4">
            <div className="border-l-4 border-blue-400 bg-blue-50 p-4 rounded-r-lg">
              <h4 className="text-sm font-semibold text-blue-800 mb-3">
                Live Tax Breakdown Preview
                <span className="text-xs text-blue-600 ml-2">(Based on current inputs)</span>
              </h4>
              
              {/* Calculate taxes based on current edit form values */}
              {(() => {
                const currentGross = parseFloat(editFormData.grossAmount) || normalizedIncome.grossAmount;
                const currentFreq = editFormData.frequency || normalizedIncome.frequency;
                const currentState = editFormData.state || normalizedIncome.location?.state;
                const previewTaxes = calculateEstimatedTaxes(currentGross, currentFreq, currentState);
                const previewTotalTax = 
                  (previewTaxes.federal || 0) + 
                  (previewTaxes.state || 0) + 
                  (previewTaxes.socialSecurity || 0) + 
                  (previewTaxes.medicare || 0);
                const previewEffectiveRate = currentGross > 0 
                  ? ((previewTotalTax / currentGross) * 100).toFixed(1) 
                  : 0;

                return (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h5 className="font-medium text-blue-800 border-b border-blue-200 pb-1 mb-2">
                        Per {formatFrequency(currentFreq)}:
                      </h5>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-blue-700">Federal:</span>
                          <span className="text-blue-800 font-medium">{formatCurrency(previewTaxes.federal)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-blue-700">State:</span>
                          <span className="text-blue-800 font-medium">{formatCurrency(previewTaxes.state)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-blue-700">Social Security:</span>
                          <span className="text-blue-800 font-medium">{formatCurrency(previewTaxes.socialSecurity)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-blue-700">Medicare:</span>
                          <span className="text-blue-800 font-medium">{formatCurrency(previewTaxes.medicare)}</span>
                        </div>
                        <div className="flex justify-between border-t border-blue-200 pt-1 font-medium">
                          <span className="text-blue-800">Total:</span>
                          <span className="text-blue-800">{formatCurrency(previewTotalTax)}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h5 className="font-medium text-blue-800 border-b border-blue-200 pb-1 mb-2">
                        Annual:
                      </h5>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-blue-700">Federal:</span>
                          <span className="text-blue-800 font-medium">{formatCurrency(previewTaxes.federal * getFrequencyDivisor(currentFreq))}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-blue-700">State:</span>
                          <span className="text-blue-800 font-medium">{formatCurrency(previewTaxes.state * getFrequencyDivisor(currentFreq))}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-blue-700">Social Security:</span>
                          <span className="text-blue-800 font-medium">{formatCurrency(previewTaxes.socialSecurity * getFrequencyDivisor(currentFreq))}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-blue-700">Medicare:</span>
                          <span className="text-blue-800 font-medium">{formatCurrency(previewTaxes.medicare * getFrequencyDivisor(currentFreq))}</span>
                        </div>
                        <div className="flex justify-between border-t border-blue-200 pt-1 font-medium">
                          <span className="text-blue-800">Total:</span>
                          <span className="text-blue-800">{formatCurrency(previewTotalTax * getFrequencyDivisor(currentFreq))}</span>
                        </div>
                        <div className="flex justify-between mt-2 text-xs">
                          <span className="text-blue-700">Effective Rate:</span>
                          <span className="text-blue-800 font-medium">{previewEffectiveRate}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
              
              <p className="text-xs text-blue-600 mt-3">
                This is a live preview based on your current inputs. Tax calculations update automatically as you edit.
              </p>
            </div>
          </div>
        </div>
      );
    } else {
      // Display Mode - Show information
    return (
      <div className="p-4 bg-gray-50 border-t border-gray-200">
        {/* Primary Details Section */}
        <Section title="Primary Details">
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <DetailItem label="Start Date">{normalizedIncome.startDate ? format(normalizedIncome.startDate, 'MMM d, yyyy') : 'Not specified'}</DetailItem>
            <DetailItem label="Employer">{normalizedIncome.employer || 'Not specified'}</DetailItem>
            <DetailItem label="Location">{normalizedIncome.location?.state || 'Not specified'}</DetailItem>
            <DetailItem label="Filing Status">{normalizedIncome.w4?.filingStatus ? getFilingStatusLabel(normalizedIncome.w4.filingStatus) : 'Not specified'}</DetailItem>
            </div>
          </div>
        </Section>
        
        {/* Payment Details Section */}
        <Section title="Payment Details">
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <DetailItem label="Gross Annual Salary">
                {formatCurrency(normalizedIncome.grossAnnualSalary || calculateAnnualAmount(normalizedIncome.grossAmount, normalizedIncome.frequency))}
              </DetailItem>
              <DetailItem label="Net Annual Salary (estimated)">{formatCurrency(annualNet)}</DetailItem>
              <DetailItem label="Payment Frequency">{formatFrequency(normalizedIncome.frequency)}</DetailItem>
              <DetailItem label={`Gross Per Pay Period (per ${formatFrequency(normalizedIncome.frequency).toLowerCase()})`}>{formatCurrency(normalizedIncome.grossAmount)}</DetailItem>
              <DetailItem label="Net Per Pay Period (estimated)">{formatCurrency(normalizedIncome.netAmount)}</DetailItem>
            </div>

            {/* Inline Equity under Payment Details */}
            {normalizedIncome.equity && Object.keys(normalizedIncome.equity).length > 0 && (
              <div className="mt-4 pt-3 border-t border-gray-200">
                <h4 className="text-xs font-semibold text-gray-600 mb-2">Equity</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {normalizedIncome.equity.rsuGrantUsd ? (
                    <DetailItem label="RSU Grant Value">{formatCurrency(normalizedIncome.equity.rsuGrantUsd)}</DetailItem>
                  ) : null}
                  {normalizedIncome.equity.totalRSUShares ? (
                    <DetailItem label="Total RSU Shares">{normalizedIncome.equity.totalRSUShares}</DetailItem>
                  ) : null}
                  {normalizedIncome.equity.vestStartDate ? (
                    <DetailItem label="Vesting Start">{format(new Date(normalizedIncome.equity.vestStartDate), 'MMM d, yyyy')}</DetailItem>
                  ) : null}
                  {(normalizedIncome.equity.vestCliffMonths || normalizedIncome.equity.vestMonths) ? (
                    <DetailItem label="Cliff / Total Months">{normalizedIncome.equity.vestCliffMonths || 0} / {normalizedIncome.equity.vestMonths || 0}</DetailItem>
                  ) : null}
                  {normalizedIncome.equity.vestCadence ? (
                    <DetailItem label="Vesting Cadence">{String(normalizedIncome.equity.vestCadence).charAt(0).toUpperCase() + String(normalizedIncome.equity.vestCadence).slice(1)}</DetailItem>
                  ) : null}
                  {normalizedIncome.equity.estimatedSharePrice ? (
                    <DetailItem label="Estimated Share Price">{formatCurrency(normalizedIncome.equity.estimatedSharePrice)}</DetailItem>
                  ) : null}
                  {normalizedIncome.equity.annualValue ? (
                    <DetailItem label="Annual Equity Value">{formatCurrency(normalizedIncome.equity.annualValue)}</DetailItem>
                  ) : null}
                </div>
              </div>
            )}

            {/* Inline Bonus under Payment Details */}
            {income?.typeSpecificDetails?.salary?.bonuses && (() => {
              const b = income.typeSpecificDetails.salary.bonuses || {};
              const hasAny = b.signing || b.retention || b.performance || b.annualTargetPercent || b.annualTargetFixed;
              if (!hasAny) return null;
              return (
                <div className="mt-4 pt-3 border-t border-gray-200">
                  <h4 className="text-xs font-semibold text-gray-600 mb-2">Bonus</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {b.signing ? (<DetailItem label="Signing Bonus">{formatCurrency(b.signing)}</DetailItem>) : null}
                    {b.retention ? (<DetailItem label="Retention Bonus">{formatCurrency(b.retention)}</DetailItem>) : null}
                    {b.performance ? (<DetailItem label="Performance Bonus">{formatCurrency(b.performance)}</DetailItem>) : null}
                    {b.annualTargetPercent ? (<DetailItem label="Target %">{`${b.annualTargetPercent}%`}</DetailItem>) : null}
                    {b.annualTargetFixed ? (<DetailItem label="Target (annual)">{formatCurrency(b.annualTargetFixed)}</DetailItem>) : null}
                  </div>
                </div>
              );
            })()}
          </div>
        </Section>

        {/* Equity Section (if available) */}
        {/* Equity now displayed within Payment Details */}
        
        {/* W4 Information Section (if available) */}
        {normalizedIncome.w4 && Object.keys(normalizedIncome.w4).length > 0 && (
          <Section title="W4 Information">
            <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <DetailItem label="Filing Status">{getFilingStatusLabel(normalizedIncome.w4.filingStatus)}</DetailItem>
                <DetailItem label="Dependents">{normalizedIncome.w4.dependents || 0}</DetailItem>
                {normalizedIncome.w4.otherIncome > 0 ? (
                  <DetailItem label="Other Income">{formatCurrency(normalizedIncome.w4.otherIncome)}</DetailItem>
                ) : null}
                {normalizedIncome.w4.deductions > 0 ? (
                  <DetailItem label="Annual Deductions">{formatCurrency(normalizedIncome.w4.deductions)}</DetailItem>
                ) : null}
                {normalizedIncome.w4.extraWithholding > 0 ? (
                  <DetailItem label="Extra Withholding / period">{formatCurrency(normalizedIncome.w4.extraWithholding)}</DetailItem>
                ) : null}
              </div>
            </div>
          </Section>
        )}
        
        {/* Deductions Section (if any) */}
        {Object.values(deductions).some(value => value > 0) && (
          <Section title="Deductions">
            <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {deductions.retirement > 0 ? (
                  <DetailItem label="Retirement (401k)">{formatCurrency(deductions.retirement)}</DetailItem>
                ) : null}
                {deductions.health > 0 ? (
                  <DetailItem label="Health Insurance">{formatCurrency(deductions.health)}</DetailItem>
                ) : null}
                {deductions.dental > 0 ? (
                  <DetailItem label="Dental Insurance">{formatCurrency(deductions.dental)}</DetailItem>
                ) : null}
                {deductions.vision > 0 ? (
                  <DetailItem label="Vision Insurance">{formatCurrency(deductions.vision)}</DetailItem>
                ) : null}
                {deductions.hsa > 0 ? (
                  <DetailItem label="HSA Contribution">{formatCurrency(deductions.hsa)}</DetailItem>
                ) : null}
                {deductions.fsa > 0 ? (
                  <DetailItem label="FSA Contribution">{formatCurrency(deductions.fsa)}</DetailItem>
                ) : null}
                {deductions.other > 0 ? (
                  <DetailItem label="Other Deductions">{formatCurrency(deductions.other)}</DetailItem>
                ) : null}
                <DetailItem label="Total Deductions">{formatCurrency(totalDeductions)}</DetailItem>
              </div>
            </div>
          </Section>
        )}

        {/* Tax Information Section */}
        <Section 
          title={<>Tax Information {taxData.isEstimated && (<span className="ml-1 text-amber-600 text-xs font-normal">(Estimated)</span>)}</>}
          right={(
            <div className="grid grid-cols-1 gap-2">
              <StatChip label="Total tax / period" value={formatCurrency(totalTax)} tone="blue" />
            </div>
          )}
        >
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <DetailItem label="Federal Withholding">{formatCurrency(taxData.federal)}</DetailItem>
              <DetailItem label="State Withholding">{formatCurrency(taxData.state)}</DetailItem>
              <DetailItem label="Social Security (EE)">{formatCurrency(taxData.socialSecurity)}</DetailItem>
              <DetailItem label="Medicare (EE)">{formatCurrency(taxData.medicare)}</DetailItem>
            </div>
          </div>
        </Section>
        
        
        
        {/* Bonus Section */}
        {/* Bonus now displayed within Payment Details */}

        {/* Additional Notes Section (if any) */}
        {normalizedIncome.description && (
          <div className="mb-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Notes</h3>
            <div className="rounded-md border border-gray-200 bg-white p-3 text-sm text-gray-900">
              {normalizedIncome.description}
            </div>
          </div>
        )}

          {/* Estimated Tax Breakdown Section - At the bottom */}
          <div className="mb-4">
            <div className="border rounded-md border-blue-200 bg-blue-50 p-4">
              <h4 className="text-sm font-semibold text-blue-800 mb-3">Estimated Tax Breakdown</h4>
              
              {/* Desktop view - Clean and organized table layout */}
              <div className="hidden md:block overflow-hidden rounded-md border border-blue-200">
                <table className="w-full text-sm">
                  <thead className="bg-blue-100/60">
                    <tr className="border-b border-blue-200">
                      <th className="text-left py-2 px-3 font-medium text-blue-800">Tax Type</th>
                      <th className="text-right py-2 px-3 font-medium text-blue-800">Per {formatFrequency(normalizedIncome.frequency)}</th>
                      <th className="text-right py-2 px-3 font-medium text-blue-800">Annual</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-blue-100">
                    <tr className="odd:bg-blue-50/40">
                      <td className="py-2 px-3">
                        <div className="text-blue-700">Federal Withholding</div>
                        <div className="text-blue-600 text-xs">Income tax withheld</div>
                      </td>
                      <td className="py-2 px-3 text-blue-800 font-medium text-right">{formatCurrency(taxData.federal)}</td>
                      <td className="py-2 px-3 text-blue-800 font-medium text-right">{formatCurrency(taxData.federal * getFrequencyDivisor(normalizedIncome.frequency))}</td>
                    </tr>
                    <tr className="odd:bg-blue-50/40">
                      <td className="py-2 px-3">
                        <div className="text-blue-700">State Withholding</div>
                        <div className="text-blue-600 text-xs">State income tax</div>
                      </td>
                      <td className="py-2 px-3 text-blue-800 font-medium text-right">{formatCurrency(taxData.state)}</td>
                      <td className="py-2 px-3 text-blue-800 font-medium text-right">{formatCurrency(taxData.state * getFrequencyDivisor(normalizedIncome.frequency))}</td>
                    </tr>
                    <tr className="odd:bg-blue-50/40">
                      <td className="py-2 px-3">
                        <div className="text-blue-700">Medicare (MED/EE)</div>
                        <div className="text-blue-600 text-xs">1.45% of gross wages</div>
                      </td>
                      <td className="py-2 px-3 text-blue-800 font-medium text-right">{formatCurrency(taxData.medicare)}</td>
                      <td className="py-2 px-3 text-blue-800 font-medium text-right">{formatCurrency(taxData.medicare * getFrequencyDivisor(normalizedIncome.frequency))}</td>
                    </tr>
                    <tr className="odd:bg-blue-50/40">
                      <td className="py-2 px-3">
                        <div className="text-blue-700">Social Security (OASDI/EE)</div>
                        <div className="text-blue-600 text-xs">6.2% of gross wages</div>
                      </td>
                      <td className="py-2 px-3 text-blue-800 font-medium text-right">{formatCurrency(taxData.socialSecurity)}</td>
                      <td className="py-2 px-3 text-blue-800 font-medium text-right">{formatCurrency(taxData.socialSecurity * getFrequencyDivisor(normalizedIncome.frequency))}</td>
                    </tr>
                    <tr className="font-medium bg-blue-100/60">
                      <td className="py-2 px-3">
                        <div className="text-blue-800">Total Tax Withholdings</div>
                        <div className="text-blue-600 text-xs">
                          Effective rate: {effectiveTaxRate}% of gross income
                        </div>
                      </td>
                      <td className="py-2 px-3 text-blue-800 font-medium text-right">{formatCurrency(totalTax)}</td>
                      <td className="py-2 px-3 text-blue-800 font-medium text-right">{formatCurrency(totalTax * getFrequencyDivisor(normalizedIncome.frequency))}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              {/* Mobile view layout */}
              <div className="md:hidden space-y-4">
                <div>
                  <h5 className="font-medium text-blue-800 border-b border-blue-200 pb-1 mb-2">
                    Per {formatFrequency(normalizedIncome.frequency)}:
                  </h5>
                  <table className="w-full text-sm">
                    <tbody>
                      <tr>
                        <td className="py-1 text-blue-700">Federal Withholding:</td>
                        <td className="py-1 text-blue-800 font-medium text-right">{formatCurrency(taxData.federal)}</td>
                      </tr>
                      <tr>
                        <td className="py-1 text-blue-700">State Withholding:</td>
                        <td className="py-1 text-blue-800 font-medium text-right">{formatCurrency(taxData.state)}</td>
                      </tr>
                      <tr>
                        <td className="py-1 text-blue-700">Medicare (MED/EE):</td>
                        <td className="py-1 text-blue-800 font-medium text-right">{formatCurrency(taxData.medicare)}</td>
                      </tr>
                      <tr>
                        <td className="py-1 text-blue-700">Social Security (OASDI/EE):</td>
                        <td className="py-1 text-blue-800 font-medium text-right">{formatCurrency(taxData.socialSecurity)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                
                <div>
                  <h5 className="font-medium text-blue-800 border-b border-blue-200 pb-1 mb-2">
                    Annual:
                  </h5>
                  <table className="w-full text-sm">
                    <tbody>
                      <tr>
                        <td className="py-1 text-blue-700">Federal Withholding:</td>
                        <td className="py-1 text-blue-800 font-medium text-right">{formatCurrency(taxData.federal * getFrequencyDivisor(normalizedIncome.frequency))}</td>
                      </tr>
                      <tr>
                        <td className="py-1 text-blue-700">State Withholding:</td>
                        <td className="py-1 text-blue-800 font-medium text-right">{formatCurrency(taxData.state * getFrequencyDivisor(normalizedIncome.frequency))}</td>
                      </tr>
                      <tr>
                        <td className="py-1 text-blue-700">Medicare (MED/EE):</td>
                        <td className="py-1 text-blue-800 font-medium text-right">{formatCurrency(taxData.medicare * getFrequencyDivisor(normalizedIncome.frequency))}</td>
                      </tr>
                      <tr>
                        <td className="py-1 text-blue-700">Social Security (OASDI/EE):</td>
                        <td className="py-1 text-blue-800 font-medium text-right">{formatCurrency(taxData.socialSecurity * getFrequencyDivisor(normalizedIncome.frequency))}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                
                <div>
                  <h5 className="font-medium text-blue-800 border-b border-blue-200 pb-1 mb-2">
                    Summary:
                  </h5>
                  <table className="w-full text-sm">
                    <tbody>
                      <tr>
                        <td className="py-1 text-blue-800 font-medium">Total Tax Withholdings:</td>
                        <td className="py-1 text-blue-800 font-medium text-right">
                          {formatCurrency(totalTax)} / {formatCurrency(totalTax * getFrequencyDivisor(normalizedIncome.frequency))}
                        </td>
                      </tr>
                      <tr>
                        <td className="py-1 text-blue-700">Effective Tax Rate:</td>
                        <td className="py-1 text-blue-800 font-medium text-right">{effectiveTaxRate}%</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              
              <p className="text-xs text-blue-600 mt-2">
                This is an estimate based on the information provided. Your actual tax withholdings may vary.
              </p>
            </div>
          </div>


      </div>
    );
    }
  };

  // Helper function to format filing status
  const getFilingStatusLabel = (status) => {
    const statusMap = {
      'SINGLE': 'Single',
      'MARRIED': 'Married Filing Jointly',
      'MARRIED_SEPARATELY': 'Married Filing Separately',
      'HEAD': 'Head of Household'
    };
    return statusMap[status] || status;
  };

  const now = new Date();
  const isInactive = !!normalizedIncome.endDate && normalizedIncome.endDate < now;

  return (
    <div className={`relative rounded-lg shadow-sm overflow-hidden border ${className} ${isInactive ? 'bg-gray-50 border-gray-100 opacity-80' : 'bg-white border-gray-200'}`}>
      {/* Header - Always Display Mode */}
      <div className="px-4 py-3 bg-gradient-to-r from-indigo-50 to-white border-b border-gray-200">
        <div className="flex justify-between items-center">
        <div className="flex items-center space-x-3">
          <div className="bg-indigo-100 p-2 rounded-lg text-indigo-600">
            <BriefcaseIcon className="h-5 w-5" />
          </div>
          <h3 className="font-medium text-gray-900">{normalizedIncome.name}</h3>
          {isInactive && (
            <span className="ml-2 inline-flex items-center text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-700">Inactive</span>
          )}
        </div>

        </div>
      </div>

      {/* Content */}
      <div className="p-3 sm:p-4 space-y-3 sm:space-y-4">
        {/* Payment Information - Mobile Responsive */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Gross Pay</div>
            <div className="text-base sm:text-lg font-semibold text-gray-900">
              {formatCurrency(normalizedIncome.grossAmount)}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {formatFrequency(normalizedIncome.frequency).toLowerCase()}
            </div>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Gross Annual</div>
            <div className="text-base sm:text-lg font-semibold text-gray-900">
              {formatCurrency(annualGross)}
            </div>
          </div>
          <div className="bg-indigo-50 p-3 rounded-lg">
            <div className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Net Pay</div>
            <div className="text-base sm:text-lg font-semibold text-indigo-600">
              {formatCurrency(normalizedIncome.netAmount)}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {formatFrequency(normalizedIncome.frequency).toLowerCase()}
            </div>
          </div>
          <div className="bg-indigo-50 p-3 rounded-lg">
            <div className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Net Annual</div>
            <div className="text-base sm:text-lg font-semibold text-indigo-600">
              {formatCurrency(annualNet)}
            </div>
          </div>
        </div>

        {/* Expand/Collapse Toggle */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pt-2 border-t border-gray-200 gap-2 sm:gap-0">
            <button
              onClick={toggleExpanded}
            className="flex items-center space-x-1 text-sm text-indigo-600 hover:text-indigo-800 transition-colors"
          >
            <span>{isExpanded ? 'Hide Details' : 'Show Details'}</span>
            {isExpanded ? (
              <ChevronUpIcon className="h-4 w-4" />
            ) : (
              <ChevronDownIcon className="h-4 w-4" />
            )}
            </button>
          
          {/* Edit/Delete/Save/Cancel Actions in Toggle Section */}
          <div className="flex items-center space-x-2">
            {isEditMode ? (
              <>
                <button
                  onClick={handleEditSave}
                  disabled={isSaving}
                  className="flex items-center space-x-1 px-3 py-2 bg-green-50 text-green-600 hover:text-green-800 rounded hover:bg-green-100 transition-colors text-xs disabled:opacity-50 min-h-[36px]"
                  aria-label="Save changes"
                  title="Save changes"
                >
                  <span>{isSaving ? 'Saving...' : 'Save'}</span>
                </button>
                <button
                  onClick={handleEditCancel}
                  className="flex items-center space-x-1 px-3 py-2 bg-gray-50 text-gray-600 hover:text-gray-800 rounded hover:bg-gray-100 transition-colors text-xs min-h-[36px]"
                  aria-label="Cancel editing"
                  title="Cancel editing"
                >
                  <span>Cancel</span>
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleEditClick}
                  className="flex items-center space-x-1 px-3 py-2 bg-indigo-50 text-indigo-600 hover:text-indigo-800 rounded hover:bg-indigo-100 transition-colors text-xs min-h-[36px]"
                  aria-label="Edit income"
                  title="Edit income"
                >
                  <PencilIcon className="h-3 w-3" />
                  <span className="hidden sm:inline">Edit</span>
                </button>
                <button
                  onClick={handleDeleteClick}
                  className="flex items-center space-x-1 px-3 py-2 bg-red-50 text-red-500 hover:text-red-700 rounded hover:bg-red-100 transition-colors text-xs min-h-[36px]"
                  aria-label="Delete income"
                  title="Delete income"
                >
                  <TrashIcon className="h-3 w-3" />
                  <span className="hidden sm:inline">Delete</span>
                </button>
              </>
            )}
            <div className="text-xs text-gray-500 hidden sm:block">
              Last updated: {format(normalizedIncome.startDate, 'MMM d, yyyy')}
            </div>
          </div>
          </div>
        </div>
        
      {/* Expanded Details Section */}
        {renderExpandedDetails()}

      {/* Edit Wizard Modal */}
      {showWizard && (
        <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50">
          <div className="bg-white p-0 rounded-lg shadow-xl max-w-md w-full">
            <EditIncomeWizard
              income={income}
              onCancel={handleWizardCancel}
              onSubmitManual={handleWizardManual}
              onSubmitRaise={handleWizardRaise}
              onSubmitJobChange={handleWizardJobChange}
            />
          </div>
        </div>
      )}

      {/* Full IncomeTypeWizard Modal for manual edit */}
      {showIncomeTypeWizard && (
        <IncomeTypeWizard
          isOpen={showIncomeTypeWizard}
          onClose={() => setShowIncomeTypeWizard(false)}
          bucketId={income.bucketId || income.bucket}
          bucketName={income.bucketName || ''}
          onSuccess={() => {
            setShowIncomeTypeWizard(false);
            if (typeof refreshIncomes === 'function') refreshIncomes();
          }}
          income={income}
        />
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center text-red-600 mb-4">
              <ExclamationTriangleIcon className="h-6 w-6 mr-2" />
              <h3 className="text-lg font-semibold">Confirm Deletion</h3>
            </div>
            
            <p className="mb-4 text-gray-600">
              Are you sure you want to delete <span className="font-semibold">{normalizedIncome.name}</span>? 
              This action cannot be undone.
            </p>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCancelDelete}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
              >
                Cancel
              </button>
            <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 rounded border border-red-200 transition-colors"
            >
                Delete
            </button>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

// Also provide a default export for backwards compatibility
export default SalaryIncomeCard; 